<template>
  <ModalConfirmation
    :class="$style.modal"
    :data-test-id="$testId('body')"
    hasBlur
    :isDisabledPrimary="invalidDate || shouldDisablePrimary"
    :isLoadingPrimary="showLoading"
    :showModal="showBulkModal"
    :title="$options.title"
    @cancel="cancelModal"
    @close="closeModal"
    @save="activateUsers"
  >
    <template v-slot:body>
      <ScheduleNotification
        :notificationAction="action"
      />
      <div
        :class="$style.textContainer"
      >
        <ParagraphBody>
          <MarkdownInline
            :data-test-id="$testId('multipleUsersText')"
            :mdTemplate="multipleUsersText"
          />
        </ParagraphBody>
        <ParagraphBody
          :data-test-id="$testId('description')"
        >
          Activating an account will give a user access to all JumpCloud provided resources.
          Accounts with scheduled activations will not be granted access until the date specified.
        </ParagraphBody>
      </div>
      <SegmentedButton
        :class="$style.activationButtons"
        name="activationButtons"
        :options="$options.activationOptions"
        :value="activationChoice"
        @input="updateActivationChoice"
      />
      <div
        :class="$style.fieldContainer"
      >
        <!-- Schedule Activations -->
        <div
          v-if="activationChoice === $options.schedule"
        >
          <FieldsScheduleActivation
            v-model="settings"
            :action="$options.activate"
            :data-test-id="$testId('FieldsScheduleActivation')"
            inMainPanel
            sendEmailWhenActivated
            showDate
            @dateIsInvalid="handleDateIsInvalid"
          />
        </div>
        <!-- Activate Now -->
        <div v-else>
          <ParagraphBody
            v-if="!eligibleUsers"
            :class="$style.noEligibleUsers"
            :data-test-id="$testId('noEligibleUsers')"
          >
            <strong>None</strong> of the selected users are eligible for activation. You
            may still schedule activations for these users at a future date and time.
          </ParagraphBody>
          <div
            v-if="suspendedCount"
            :class="{
              [$style.withDivider]: stagedWithPasswordCount || stagedPendingPasswordCount
            }"
          >
            <HeadingExtraSmall
              :data-test-id="$testId('restoreHeading')"
            >
              Account restored email
            </HeadingExtraSmall>
            <ParagraphBody
              :class="[$style.message, $style.restorationMessage]"
              :data-test-id="$testId('restorationMessage')"
              :hasMargin="false"
            >
              <strong>{{ suspendedWithPasswordCount }} of {{ suspendedCount }}</strong>
              {{ getAccountRestoredEmailText }}
            </ParagraphBody>
          </div>
          <div
            v-if="stagedWithPasswordCount"
            :class="{
              [$style.withDivider]: stagedPendingPasswordCount
            }"
          >
            <HeadingExtraSmall
              :data-test-id="$testId('welcomeHeading')"
            >
              Welcome email
            </HeadingExtraSmall>
            <ParagraphBody
              :class="$style.message"
              :data-test-id="$testId('stagedWithPassword')"
              :hasMargin="false"
            >
              <strong>{{ stagedWithPasswordCount }}</strong>
              {{ getWelcomeEmailText }}
            </ParagraphBody>
            <Checkbox
              v-model="settings.isWelcomeEmailChecked"
              :data-test-id="$testId('welcomeGroupCheckbox')"
              label="Send welcome email"
            />
            <TransitionWrapper
              name="slideVertical"
            >
              <div
                v-if="settings.isWelcomeEmailChecked"
                :class="$style.radioGroup"
              >
                <RadioGroup
                  v-model="settings.welcomeEmailOption"
                  :class="$style.choices"
                  :data-test-id="$testId('welcomeEmail')"
                  label=""
                  name="welcomeGroupRadio"
                  :options="[{'label':'Send to alternate email','value':1 },
                             {'label':'Send to company email','value':2 } ]"
                  value="1"
                />
                <TooltipIcon
                  :class="$style.tooltip"
                  :data-test-id="$testId('welcomeGroupTooltip')"
                  :hasIndicator="true"
                  :isDark="false"
                  orientation="right"
                  :text="$options.tooltipContent"
                />
              </div>
            </TransitionWrapper>
          </div>
          <div
            v-if="stagedPendingPasswordCount"
          >
            <HeadingExtraSmall
              :data-test-id="$testId('activateHeading')"
            >
              Activation email
            </HeadingExtraSmall>
            <ParagraphBody
              :class="$style.message"
              :data-test-id="$testId('stagedPendingPassword')"
              :hasMargin="false"
            >
              <strong>{{ stagedPendingPasswordCount }}</strong>
              {{ getActivationEmailText }}
            </ParagraphBody>
            <Checkbox
              v-model="settings.isAlternateEmailChecked"
              :data-test-id="$testId('alternateGroupCheckbox')"
              label="Send activation email"
            />
            <TransitionWrapper
              name="slideVertical"
            >
              <div
                v-if="settings.isAlternateEmailChecked"
                :class="$style.radioGroup"
              >
                <RadioGroup
                  v-model="settings.alternateEmailOption"
                  :class="$style.choices"
                  :data-test-id="$testId('alternateEmail')"
                  label=""
                  name="alternateGroupRadio"
                  :options="[{'label':'Send to alternate email','value':1 },
                             {'label':'Send to company email','value':2 } ]"
                  :showValidationMessage="false"
                  value="1"
                />
                <TooltipIcon
                  :class="$style.tooltip"
                  :data-test-id="$testId('alternateGroupTooltip')"
                  :hasIndicator="true"
                  :isDark="false"
                  orientation="right"
                  :text="$options.tooltipContent"
                />
              </div>
            </TransitionWrapper>
          </div>
          <ParagraphBody
            v-if="eligibleUsers && activationChoice === $options.now"
            :class="$style.eligibleUsers"
            :data-test-id="$testId('activationText')"
            weight="bold"
          >
            {{ getAccountActivationText }}
          </ParagraphBody>
        </div>
      </div>
    </template>
    <template v-slot:footerAddition>
      <Link
        :href="$options.kbLink"
        isExternal
        target="_blank"
        text="Learn how to customize email templates"
      />
    </template>
  </ModalConfirmation>
</template>

<script>
import {
  Checkbox,
  HeadingExtraSmall,
  Link,
  MarkdownInline,
  ModalConfirmation,
  ParagraphBody,
  RadioGroup,
  SegmentedButton,
  TooltipIcon,
  TransitionWrapper,
} from '@jumpcloud/ui-components';
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import { userStateActionType } from 'client/systemusers/constants/SystemUserConstants';
import FieldsScheduleActivation from 'client/systemusers/components/FieldsScheduleActivation.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalHelper from 'client/systemusers/Modals/ModalHelper';
import ScheduleNotification from 'client/systemusers/components/ScheduleNotification.vue';
import pluralize from 'pluralize';

const systemUserCollection = 'SystemUserCollection';

const {
  activationOptionTypes,
  setDefaultActionDate,
} = ModalHelper;

export default {
  name: 'BulkActivateUserModal',

  components: {
    Checkbox,
    FieldsScheduleActivation,
    HeadingExtraSmall,
    Link,
    MarkdownInline,
    ModalConfirmation,
    ParagraphBody,
    RadioGroup,
    ScheduleNotification,
    SegmentedButton,
    TooltipIcon,
    TransitionWrapper,
  },

  props: {
    showBulkModal: Boolean,
  },

  data() {
    return {
      action: 'activate',
      activationChoice: this.$options.schedule,
      isDateInvalid: false,
      settings: {
        alternateEmailOption: 1,
        emailSettings: {
          sendWelcomeEmail: true,
          welcomeEmailAddress: '',
        },
        eventDate: setDefaultActionDate(userStateActionType.activate),
        isAlternateEmailChecked: false,
        isWelcomeEmailChecked: false,
        welcomeEmailOption: 2,
      },
      shouldDisablePrimary: false,
      showLoading: false,
      stagedPendingPasswordCount: 0,
      stagedWithPasswordCount: 0,
      suspendedCount: 0,
      suspendedWithPasswordCount: 0,
    };
  },

  computed: {
    ...mapGetters(systemUserCollection, {
      getStagedUsers: 'getStagedUsers',
      getSuspendedUsers: 'getSuspendedUsers',
      inactiveUsers: 'getInactiveUsers',
      ineligibleForActivation: 'getUsersIneligibleForActivation',
      selectedItems: 'getSelectedItems',
    }),

    eligibleCount() {
      const ineligibleCount = this.ineligibleForActivation?.activatedUsers?.length || 0;
      return this.selectedItems.length - ineligibleCount;
    },

    eligibleUsers() {
      const count = this.suspendedCount
        + this.stagedPendingPasswordCount
        + this.stagedWithPasswordCount;
      return count !== 0;
    },

    getAccountActivationText() {
      const noun = pluralize('user', this.selectedItems.length);

      return `Are you sure you want to activate the selected ${noun}?`;
    },

    getAccountRestoredEmailText() {
      return 'suspended users will receive an account restored email. Only suspended users with an active password will get an email.';
    },

    getActivationEmailText() {
      const noun = pluralize('user', this.stagedPendingPasswordCount);
      const verb = pluralize('is', this.stagedPendingPasswordCount);

      return `staged ${noun} ${verb} eligible to receive an activation email.`;
    },

    getWelcomeEmailText() {
      const noun = pluralize('user', this.stagedWithPasswordCount);
      const verb = pluralize('is', this.stagedWithPasswordCount);

      return `staged ${noun} ${verb} eligible to receive a welcome email.`;
    },

    invalidDate() {
      return this.isDateInvalid && this.activationChoice === this.$options.schedule;
    },

    multipleUsersText() {
      const selectedCount = this.selectedItems.length;

      const text = this.activationChoice === this.$options.now
        ? `**${this.eligibleCount} of ${selectedCount}** selected users are eligible.`
        : '**All** selected users are eligible.';

      return text;
    },
  },

  activationOptions: [
    // These need to be in this order for the button group
    activationOptionTypes.schedule,
    activationOptionTypes.now,
  ],

  activate: userStateActionType.activate,

  kbLink: KbArticleUrls.emailCustomization,

  now: activationOptionTypes.now.value,
  schedule: activationOptionTypes.schedule.value,

  title: 'Activate Users',

  tooltipContent: "If the user doesn't have an alternate email, it will automatically be sent to their company email",

  mounted() {
    this.getCounts();
  },

  methods: {
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerSuccessNotification',
    ]),

    ...mapActions(systemUserCollection, [
      'bulkActivateUsers',
      'scheduleUserStateChange',
      'unselectItems',
      'updateItems',
      'updateModifiedItemCount',
      'updateNextScheduledEvent',
    ]),

    async activateUsers() {
      const numUsers = this.selectedItems.length;
      this.showLoading = true;

      if (this.activationChoice === this.$options.schedule) {
        const results = await this.scheduleUserStateChange({
          payload: {
            action: this.$options.activate,
            emailSettings: this.settings.emailSettings,
            eventDate: this.settings.eventDate,
            ids: this.selectedItems,
          },
        });

        const { failedJobs, scheduledUsers } = results;

        if (scheduledUsers.length) {
          // Update scheduled events list so that event badge displays
          this.updateNextScheduledEvent(scheduledUsers);
          this.triggerSuccessNotification({
            title: 'Scheduled',
            message: `Activation scheduled for ${scheduledUsers.length} of ${numUsers} users.`,
          });
        } else if (failedJobs.length) {
          this.triggerErrorNotification({
            title: 'Error',
            message: `Unable to schedule activation for ${failedJobs.length} ${pluralize('users', failedJobs.length)}.`,
          });
        }
      } else if (this.activationChoice === this.$options.now) {
        const { activatedUsers } = this.ineligibleForActivation || {};
        this.unselectItems({ ids: activatedUsers.map(item => item.id) });

        let responseMessage = `Error updating ${pluralize('user', numUsers)}.`;

        const { stagedUsers, suspendedUsers } = this.inactiveUsers;
        const response = await this.bulkActivateUsers({
          stagedUsers,
          suspendedUsers,
          settings: this.settings,
        });

        if (get(response, 'numSuccess')) {
          responseMessage = (numUsers === 1)
            ? '1 user activated successfully.'
            : `${response.numSuccess} of ${numUsers} users activated successfully.`;
          this.unselectItems({ ids: this.selectedItems });
          this.triggerSuccessNotification({
            title: 'Saved',
            message: responseMessage,
          });
        } else {
          this.triggerErrorNotification({
            title: 'Error',
            message: responseMessage,
          });
        }
      }

      // Let any components watching know that this user has been updated
      this.updateModifiedItemCount();

      this.unselectItems({ ids: this.selectedItems });
      this.showLoading = false;
      this.closeModal();
    },

    cancelModal() {
      this.$emit('cancel');
    },

    closeModal() {
      this.$emit('close');
    },

    getCounts() {
      const { stagedPendingPassword, stagedWithPassword } = this.getStagedUsers;
      const { suspendedUsers, suspendedWithPassword } = this.getSuspendedUsers;

      this.stagedPendingPasswordCount = stagedPendingPassword.length;
      this.stagedWithPasswordCount = stagedWithPassword.length;
      this.suspendedCount = suspendedUsers.length;
      this.suspendedWithPasswordCount = suspendedWithPassword.length;
    },

    handleDateIsInvalid(value) {
      this.isDateInvalid = value;
    },

    updateActivationChoice(value) {
      this.activationChoice = value;
      this.shouldDisablePrimary = value === this.$options.now
        ? !this.eligibleUsers
        : false;
    },
  },
};
</script>

<style module scoped>
.modal.modal {
  --modal-width: 49rem;
  --modal-max-width: 100%;

  padding-left: var(--jc-spacer-x-small);
}

.withDivider {
  border-bottom: var(--jc-border);
  margin-bottom: var(--jc-spacer);
  padding-bottom: var(--jc-spacer);
}

/* Reach in and set the max-width so that the button text doesn't wrap */
.activationButtons /deep/ div {
  max-width: 50ch;
}

.message.message {
  margin-bottom: var(--jc-spacer);
  margin-top: var(--jc-spacer-negative);
}

.restorationMessage.restorationMessage {
  margin-bottom: 0;
}

.choices.choices {
  display: inline;
  margin-bottom: var(--jc-spacer-negative-medium);
  margin-left: var(--jc-spacer);
}

.radioGroup {
  display: flex;
  margin-left: var(--jc-spacer-small);
  margin-top: var(--jc-spacer);
}

.tooltip {
  flex: 0 0 auto;
  top: -0.625em;
}

.fieldContainer {
  display: grid;
  min-height: var(--jc-size-20);
}

.textContainer {
  min-height: var(--jc-size-6);
}

.eligibleUsers {
  padding-top: var(--jc-spacer-medium);
}

.noEligibleUsers {
  padding-top: var(--jc-spacer-medium);
}
</style>
